import React, { useContext } from 'react';
import { BpaContext, IBpaContext, IBpaSet } from '../../bpaState/bpaContext';
import { bpaPaymentSystem } from 'appConstants';
import { FieldData } from 'rc-field-form/lib/interface';
import { useTranslation } from 'react-i18next';
import { FormRow } from 'components/EntityForm/FormRow';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { Icon, IconName } from 'components/Icon/Icon';
import Text from 'components/Text/Text';
import { SetNameField } from './SetNameField';
import { RecipientAccountNumberField } from './RecipientAccountNumberField';
import { RecipientNameField } from './RecipientNameField';
import { RecipientBankField } from './RecipientBankField';
import { RecipientBankCityField } from './RecipientBankCityField';
import { RecipientInnField } from './RecipientInnField';
import { RecipientBicField } from './RecipientBicField';
import { RecipientCorrespondentAccountField } from './RecipientCorrespondentAccountField';
import { PaymentDetailsField } from './PaymentDetailsField';
import { CardContractNumberField } from './CardContractNumberField';
import { SbpContractNumberField } from './SbpContractNumberField';
import { Tariff } from './Tariff';
import classNames from 'classnames';

interface IProps {
	bpaSet: IBpaSet;
	allFields: FieldData[];
}

export const BpaSet: React.FC<IProps> = ({ bpaSet, allFields }) => {
	const { t } = useTranslation('form');
	const {
		deleteBpaBlock,
		toggleIsOpenBpaBlock,
		changeName,
		changeRecipientAccountNumber,
		changeRecipientName,
		changeRecipientBank,
		changeRecipientBankCity,
		changeRecipientInn,
		changeRecipientBic,
		changeRecipientCorrespondentAccount,
		changePaymentDetails,
		changeCardContractNumber,
		changeSbpContractNumber,
		addTariff,
	} = useContext<IBpaContext>(BpaContext);
	const setId = bpaSet.id;
	const ids = [setId, ...bpaSet.tariffs.flatMap((tariff) => [tariff.id, ...tariff.data.map((el) => el.id)])];
	const hasErrors = allFields.some(
		(field) => !!field.errors?.length && ids.some((id) => String(field.name).endsWith(`-${id}`)),
	);

	return (
		<div
			className="p-4 border border-solid border-purple-02-new rounded flex flex-col gap-4 bg-white-00-new"
			data-test="BpaSet">
			<div className="flex items-center gap-4">
				<Text
					className="flex-1"
					translationKey={bpaSet.name}
					theme={hasErrors ? 'error' : 'neutralDark'}
					size="ml"
					weight="bold"
				/>
				<NewButton
					variant="ghost"
					size="small"
					icon={true}
					iconLeft={<Icon name={bpaSet.isOpenSet ? IconName.chevronUp : IconName.chevronDown} size="lg" />}
					ariaLabel={t(bpaSet.isOpenSet ? 'hideBlockButton' : 'expandBlockButton')}
					testId="BpaSetToggle"
					onClick={() => toggleIsOpenBpaBlock(setId)}
				/>
			</div>
			<div className={classNames('flex flex-col gap-4', { hidden: !bpaSet.isOpenSet })}>
				<SetNameField setId={setId} onChange={(value) => changeName({ setId, value })} />
				<Text translationNameSpace="form" translationKey="requisitesHeading" size="m" weight="bold" />
				<FormRow>
					<RecipientAccountNumberField
						setId={setId}
						onChange={(value) => changeRecipientAccountNumber({ setId, value })}
					/>
					<RecipientNameField setId={setId} onChange={(value) => changeRecipientName({ setId, value })} />
				</FormRow>
				<FormRow>
					<RecipientBankField setId={setId} onChange={(value) => changeRecipientBank({ setId, value })} />
					<RecipientBankCityField
						setId={setId}
						onChange={(value) => changeRecipientBankCity({ setId, value })}
					/>
				</FormRow>
				<FormRow>
					<RecipientInnField setId={setId} onChange={(value) => changeRecipientInn({ setId, value })} />
					<RecipientBicField setId={setId} onChange={(value) => changeRecipientBic({ setId, value })} />
				</FormRow>
				<FormRow>
					<RecipientCorrespondentAccountField
						setId={setId}
						onChange={(value) => changeRecipientCorrespondentAccount({ setId, value })}
					/>
					<PaymentDetailsField setId={setId} onChange={(value) => changePaymentDetails({ setId, value })} />
				</FormRow>
				<FormRow>
					<CardContractNumberField
						setId={setId}
						onChange={(value) => changeCardContractNumber({ setId, value })}
					/>
					<SbpContractNumberField
						setId={setId}
						onChange={(value) => changeSbpContractNumber({ setId, value })}
					/>
				</FormRow>
				<div className="flex items-center gap-2">
					<Text
						className="flex-1"
						translationNameSpace="form"
						translationKey="tariffHeading"
						size="m"
						weight="bold"
					/>
					<NewButton
						size="small"
						iconLeft={<Icon name={IconName.plus} size="lg" />}
						disabled={bpaSet.tariffs.length >= bpaPaymentSystem.length}
						testId="AddTariffBtn"
						onClick={() => addTariff(setId)}>
						<Text translationNameSpace="form" translationKey="addTariffButton" asFragment={true} />
					</NewButton>
				</div>
				{bpaSet.tariffs.map((el) => (
					<Tariff key={el.id} setId={setId} tariff={el} tariffs={bpaSet.tariffs} />
				))}
			</div>
			<div className="flex justify-end">
				<NewButton
					variant="outline"
					color="red"
					size="small"
					iconLeft={<Icon name={IconName.delete} size="lg" />}
					testId="DeleteBpaSetBtn"
					onClick={() => deleteBpaBlock(setId)}>
					<Text translationNameSpace="form" translationKey="deleteSetButton" asFragment={true} />
				</NewButton>
			</div>
		</div>
	);
};
